import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import React, { FC } from 'react';
import { Control } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';

import { ChevronDownIcon } from '../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import {
  ComboboxButton,
  ComboboxOptions,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../../../../../../components/combobox/index';
import Loader from '../../../../../../components/loader/index';
import { useScreenSize, VestingTaskHooks } from '../../../../../../hooks/index';
import { VestingTasksResponse } from '../../../../../../hooks/vesting-task/index';
import { SharePlanType } from '../../../../../../types/pool-plans.types';
import { VestingTask } from '../../../../../../types/vesting-tasks.types';
import { FormSchema } from '../terminated-exercise-form.validation';

type GrantItemsListProps = {
  control: Control<FormSchema>;
  data: VestingTask[];
  fetchNextPage: (
    options?: FetchNextPageOptions,
  ) => Promise<InfiniteQueryObserverResult<InfiniteData<VestingTasksResponse, unknown>, Error>>;
  hasNextPage: boolean;
  isLoading: boolean;
  name: string;
  selectedGrantIds?: string[];
};

const GrantItemsList: FC<GrantItemsListProps> = ({
  data,
  control,
  name,
  hasNextPage,
  fetchNextPage,
  isLoading,
  selectedGrantIds,
}) => {
  const { scrollableTarget } = useScreenSize();
  const filteredData = data.filter(
    ({ id, vestedSharesCount, exercisedCount }) =>
      !selectedGrantIds?.includes(id) && vestedSharesCount - exercisedCount > 0,
  );
  return (
    <InfiniteScroll
      className="flex w-full flex-col gap-2"
      dataLength={data.length || 0}
      hasMore={hasNextPage}
      loader={<Loader />}
      next={fetchNextPage}
      scrollableTarget={scrollableTarget}
    >
      {filteredData.length > 0 ? (
        <>
          {filteredData.map(({ grantItem, id, exercisedCount, vestedSharesCount }) => (
            <FormComboboxOption
              className="max-h-[100px] w-full"
              control={control}
              key={id}
              name={name as keyof FormSchema}
              value={
                {
                  id,
                  grantItem,
                  exercisedCount,
                  vestedSharesCount,
                } as unknown as FormSchema[keyof FormSchema]
              }
            >
              <span className="truncate text-sm uppercase text-gray-700">
                {grantItem?.id?.split('-')[0]}
              </span>
            </FormComboboxOption>
          ))}
        </>
      ) : isLoading ? (
        <span className="py-[6px] text-sm font-normal text-gray-700">
          <AppFormattedMessage id="LOADING" />
        </span>
      ) : (
        <span className="py-[6px] text-sm font-normal text-gray-700">
          <AppFormattedMessage id="NO_GRANTS_FOUND" />
        </span>
      )}
    </InfiniteScroll>
  );
};

export type GrantComboboxProps = {
  companyId: string;
  control: Control<FormSchema>;
  name: string;
  selectedGrantIds?: string[];
  sharePlanType: SharePlanType;
  stakeholderId: string;
};

export const GrantCombobox: FC<GrantComboboxProps> = ({
  control,
  companyId,
  name,
  stakeholderId = '',
  sharePlanType,
  selectedGrantIds,
}) => {
  const { vestingTasks, fetchNextPage, hasNextPage, isLoading } = VestingTaskHooks.useVestingTasks({
    companyId,
    stakeholderId,
    sharePlanType,
    exercisable: true,
  });

  const {
    vestingTasks: phantomVestingTasks,
    fetchNextPage: phantomFetchNextPage,
    hasNextPage: phantomHasNextPage,
    isLoading: phantomIsLoading,
  } = VestingTaskHooks.usePhantomVestingTasks({
    companyId,
    stakeholderId,
  });

  const vestingTasksLength =
    sharePlanType === SharePlanType.PHANTOM ? phantomVestingTasks?.length : vestingTasks?.length;

  return (
    <>
      <FormCombobox
        className="relative w-full min-w-[130px]"
        control={control}
        disabled={!stakeholderId}
        name={name as keyof FormSchema}
      >
        <div className="relative">
          <FormComboboxInput
            className={'bg-gray-900'}
            control={control}
            customValue={(value) =>
              vestingTasksLength > 0 ? (value as VestingTask)?.grantItem?.id?.split('-')[0] : ''
            }
            icon={<ChevronDownIcon />}
            name={name as keyof FormSchema}
            readOnly
            toUppercase
            wrapperClassName="h-10"
          />
          <ComboboxButton className="absolute left-0 top-0 z-20 h-full w-full" />
        </div>
        <ComboboxOptions className="z-20 max-h-[157px] w-[200px] overflow-x-hidden">
          <GrantItemsList
            control={control}
            data={
              sharePlanType === SharePlanType.PHANTOM ? phantomVestingTasks : vestingTasks || []
            }
            fetchNextPage={
              sharePlanType === SharePlanType.PHANTOM ? phantomFetchNextPage : fetchNextPage
            }
            hasNextPage={sharePlanType === SharePlanType.PHANTOM ? phantomHasNextPage : hasNextPage}
            isLoading={sharePlanType === SharePlanType.PHANTOM ? phantomIsLoading : isLoading}
            name={name}
            selectedGrantIds={selectedGrantIds}
          />
        </ComboboxOptions>
      </FormCombobox>
    </>
  );
};
